import { Form, Formik } from "formik";
import { useCensusAndCurrentInformationContext } from "pages/CensusAndCurrentInformation/CensusAndCurrentInformation";
import {
  BACK_BUTTON_TEXT,
  CURRENT_HEALTH_INSURANCE,
  LOADING_BUTTON_TEXT,
  NEXT_BUTTON_TEXT,
} from "pages/CensusAndCurrentInformation/utils/constants";
import React, { useEffect, useState } from "react";
import { CurrentInsuranceType } from "types";
import FooterButton from "uicomponents/FooterButton";
import {
  trackFormError,
  trackFormSubmission,
  trackPageViewUserStep,
  trackSaveAndContinueUserStep,
} from "utils/analytics/trackEvent";
import CurrentInsuranceComponent from "./components/CurrentInsuranceComponent";
import styles from "./CurrentInsurance.module.css";
import { currentInsuranceFormValues } from "pages/CensusAndCurrentInformation/utils/initialFormValues";
import { currentInsuranceValidationSchema } from "pages/CensusAndCurrentInformation/utils/validationSchema";
import {
  useUpdateCurrentInsuranceMutation,
  useUpdateProspectInfoMutation,
  useUpdateStepMutation,
} from "types/generated/operations";
import Alert from "uicomponents/Alert";
import {
  convertStringIntoBool,
  ConvertToISODate,
  errorMessageMap,
} from "utils/helper";
import { getProspectUuid } from "utils/idHelpers";
import { CurrentInsuranceFormType } from "pages/CensusAndCurrentInformation/utils/formTypes";
import { mapBEStepstoMatchFE } from "pages/CensusAndCurrentInformation/utils/helper";
import newstyles from "../../CensusAndCurrentInformation.module.css";
import {
  STEPS_WITH_COBRA,
  STEPS_WITHOUT_COBRA,
} from "pages/CensusAndCurrentInformation/utils/constants";
import Stepper from "uicomponents/Stepper";
import { getStepNumberFromURL } from "utils/helper";
import Error from "uicomponents/Error";

interface ICurrentInsuranceProps {
  data: CurrentInsuranceType;
}

const CurrentInsurance = (props: ICurrentInsuranceProps): JSX.Element => {
  useEffect(() => {
    window.scrollTo(0, 0);
    trackPageViewUserStep(6, "current insurance");
  }, []);

  const { data } = props;
  const hasCobra = data?.healthInsuranceInformation?.cobraUsage;
  const updateTwiceToSkipCobra =
    !hasCobra && data?.healthInsuranceApplication?.[0]?.step === 6;

  const { previousStep, nextStep, stepperNextStep } =
    useCensusAndCurrentInformationContext();

  const STEPS = hasCobra ? STEPS_WITH_COBRA : STEPS_WITHOUT_COBRA;

  const initialFormValues = currentInsuranceFormValues(data);

  const [isError, setIsError] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const [updateCurrentInsurance, { loading: updateCurrentInsuranceLoading }] =
    useUpdateCurrentInsuranceMutation();
  const [updateProspectInfo, { loading: updateProspectInfoLoading }] =
    useUpdateProspectInfoMutation();
  const [updateStep, { loading: updateStepLoading }] = useUpdateStepMutation();

  const applicationStep = data?.healthInsuranceApplication?.[0]?.step;
  const currentStep = getStepNumberFromURL();
  const isInValidStep = applicationStep && currentStep > applicationStep;

  if (isInValidStep) {
    return <Error />;
  }

  const handleSubmit = async (values: CurrentInsuranceFormType) => {
    try {
      const currentStep = getStepNumberFromURL();
      await updateCurrentInsurance({
        variables: {
          uuid: getProspectUuid(),
          controlledGroup: convertStringIntoBool(values.controlledGroup),
          startingCoverageDate: values.startingCoverageDate
            ? ConvertToISODate(values.startingCoverageDate)
            : undefined,
          isOnPeo: convertStringIntoBool(values.isOnPeo),
          currentPeo: values.currentPeo,
          currentPeoOther: values.currentPeoOther,
        },
      });

      await updateProspectInfo({
        variables: {
          uuid: getProspectUuid(),
          sicCode: values.sicCode,
          yearFounded: Number(values.yearFounded),
          companyName: data?.companyName,
          dba: data?.dba,
        },
      });
      if (data?.healthInsuranceApplication?.[0]?.step === currentStep) {
        await updateStep({
          variables: {
            uuid: data?.healthInsuranceApplication?.[0].uuid,
            step: data?.healthInsuranceApplication?.[0]?.step + 1,
          },
        });
        trackSaveAndContinueUserStep(currentStep, "Current Employees");
        if (updateTwiceToSkipCobra) {
          await updateStep({
            variables: {
              uuid: data?.healthInsuranceApplication?.[0].uuid,
              step: 8,
            },
          });
          trackFormSubmission(7, "CensusAndCurrentInfo");
        }
      }
      nextStep();
    } catch (error: any) {
      const errorMessage: string = error?.message;
      errorMessageMap[errorMessage] &&
        setErrorMessage(errorMessageMap[errorMessage]);
      trackFormError(error, errorMessage);
      setIsError(true);
      return;
    }
  };

  return (
    <div className={newstyles.censusAndCurrentInformationformContainer}>
      <Stepper
        steps={STEPS}
        currentStep={mapBEStepstoMatchFE(getStepNumberFromURL())}
        status={
          mapBEStepstoMatchFE(
            data?.healthInsuranceApplication?.[0]?.step || 0
          ) || 8
        }
        onClick={(stepNumber) => stepperNextStep(stepNumber)}
      />

      <div className={newstyles.censusAndCurrentInformationformDiv}>
        <div className={styles.title}>{CURRENT_HEALTH_INSURANCE.TITLE}</div>
        <Formik
          initialValues={initialFormValues}
          validationSchema={currentInsuranceValidationSchema}
          onSubmit={handleSubmit}
        >
          {() => (
            <Form>
              <CurrentInsuranceComponent data={data} />
              <FooterButton
                nextButtonText={
                  updateCurrentInsuranceLoading ||
                  updateProspectInfoLoading ||
                  updateStepLoading
                    ? LOADING_BUTTON_TEXT
                    : NEXT_BUTTON_TEXT
                }
                backButtonText={BACK_BUTTON_TEXT}
                disabled={
                  updateStepLoading ||
                  updateCurrentInsuranceLoading ||
                  updateProspectInfoLoading
                }
                handleBackClick={previousStep}
              />
              {isError && <Alert message={errorMessage} />}
            </Form>
          )}
        </Formik>
      </div>
    </div>
  );
};

export default CurrentInsurance;
